import React, { useMemo, useState } from "react";

export interface TableColumn {
  key: string;
  label: string;
  type?: "text" | "number" | "actions" | "checkbox";
  width?: string;
  cell?: ({ row }: { row: any }) => React.ReactNode;
}

export interface TableAction {
  label: string;
  onClick: (item: any) => void;
  variant: "edit" | "delete" | "custom";
  className?: string;
}

interface DataTableProps {
  title: string;
  data: any[];
  columns: TableColumn[];
  actions?: TableAction[];
  loading?: boolean;
  emptyMessage?: string;
  pagination?: {
    enabled: boolean;
    itemsPerPage?: number;
    showItemsPerPageSelector?: boolean;
  };
  filters?: {
    enabled: boolean;
    filterKey?: string;
    filterLabel?: string;
    filterOptions?: { value: string; label: string }[];
    filterType?: "text" | "select";
  };
  selection?: {
    enabled: boolean;
    onSelectionChange?: (selectedItems: any[]) => void;
  };
  onNavigateToPage?: (page: number) => void;
  externalPage?: number;
}

export const DataTable = React.forwardRef<any, DataTableProps>(
  (
    {
      title,
      data,
      columns,
      actions = [],
      loading = false,
      emptyMessage = "No hay registros.",
      pagination = { enabled: false },
      filters = { enabled: false },
      selection = { enabled: false },
      onNavigateToPage,
      externalPage,
    },
    ref
  ) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [filterValue, setFilterValue] = useState("");
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
      // Intentar recuperar la preferencia del usuario desde localStorage
      if (pagination.showItemsPerPageSelector) {
        const saved = localStorage.getItem("dataTableItemsPerPage");
        if (saved) {
          const parsed = parseInt(saved);
          // Verificar que sea un valor válido (10, 25, 50, 100)
          if ([10, 25, 50, 100].includes(parsed)) {
            return parsed;
          }
        }
      }
      return pagination.itemsPerPage || 10;
    });

    // Exponer funciones al componente padre
    React.useImperativeHandle(ref, () => ({
      setCurrentPage,
      getCurrentPage: () => currentPage,
      getItemsPerPage: () => itemsPerPage,
    }));

    // Filtrar datos
    const filteredData = useMemo(() => {
      if (!filters.enabled || !filterValue) {
        return data;
      }

      return data.filter((item) => {
        if (filters.filterKey) {
          const value = item[filters.filterKey];
          if (filters.filterType === "select") {
            // Para select, comparación exacta
            return String(value) === filterValue;
          } else {
            // Para text, búsqueda parcial
            if (typeof value === "string") {
              return value.toLowerCase().includes(filterValue.toLowerCase());
            }
            return String(value)
              .toLowerCase()
              .includes(filterValue.toLowerCase());
          }
        }
        return true;
      });
    }, [data, filterValue, filters]);

    // Paginar datos
    const paginatedData = useMemo(() => {
      if (!pagination.enabled) {
        return filteredData;
      }

      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;

      const result = filteredData.slice(startIndex, endIndex);

      return result;
    }, [filteredData, currentPage, itemsPerPage, pagination.enabled]);

    // Calcular información de paginación
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, filteredData.length);

    // Ajustar página actual si está fuera del rango válido
    React.useEffect(() => {
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      }
    }, [currentPage, totalPages]);

    // Resetear página cuando cambia el filtro o elementos por página
    React.useEffect(() => {
      setCurrentPage(1);
    }, [filterValue, itemsPerPage]);

    // Guardar la preferencia del usuario en localStorage cuando cambie
    React.useEffect(() => {
      if (pagination.showItemsPerPageSelector) {
        localStorage.setItem("dataTableItemsPerPage", itemsPerPage.toString());
      }
    }, [itemsPerPage, pagination.showItemsPerPageSelector]);

    // Manejar selección de elementos
    const handleSelectAll = (checked: boolean) => {
      if (checked) {
        setSelectedItems([...paginatedData]);
      } else {
        setSelectedItems([]);
      }
    };

    const handleSelectItem = (item: any, checked: boolean) => {
      if (checked) {
        setSelectedItems([...selectedItems, item]);
      } else {
        setSelectedItems(selectedItems.filter((i) => i.id !== item.id));
      }
    };

    // Notificar cambios en la selección
    React.useEffect(() => {
      if (selection.onSelectionChange) {
        selection.onSelectionChange(selectedItems);
      }
    }, [selectedItems, selection]);

    // Exponer itemsPerPage actual para que el componente padre pueda usarlo
    React.useEffect(() => {
      window.dispatchEvent(
        new CustomEvent("dataTableItemsPerPageChanged", {
          detail: { itemsPerPage },
        })
      );
    }, [itemsPerPage]);

    // Verificar si todos los elementos de la página están seleccionados
    const allSelected =
      paginatedData.length > 0 &&
      paginatedData.every((item) =>
        selectedItems.some((selected) => selected.id === item.id)
      );

    // Verificar si algunos elementos están seleccionados
    const someSelected = paginatedData.some((item) =>
      selectedItems.some((selected) => selected.id === item.id)
    );

    const renderCell = (column: TableColumn, item: any) => {
      if (column.type === "checkbox") {
        const isSelected = selectedItems.some(
          (selected) => selected.id === item.id
        );
        return (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => handleSelectItem(item, e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
        );
      }

      if (column.type === "actions") {
        return (
          <div className="flex space-x-2">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={() => action.onClick(item)}
                className={`px-3 py-1 text-sm rounded ${
                  action.variant === "edit"
                    ? "bg-blue-500 text-white hover:bg-blue-600"
                    : action.variant === "delete"
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : "bg-gray-500 text-white hover:bg-gray-600"
                } ${action.className || ""}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        );
      }

      if (column.cell) {
        return column.cell({ row: item });
      }

      return item[column.key] || "";
    };

    if (loading) {
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {selection.enabled && selectedItems.length > 0 && (
              <div className="text-sm text-gray-600">
                {selectedItems.length} elemento(s) seleccionado(s)
              </div>
            )}
          </div>
        </div>

        {/* Filtros */}
        {filters.enabled && (
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">
                {filters.filterLabel || "Filtrar:"}
              </label>
              {filters.filterType === "select" ? (
                <select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                >
                  <option value="">Todos</option>
                  {filters.filterOptions?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  placeholder="Buscar..."
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                />
              )}
            </div>
          </div>
        )}

        {/* Tabla */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {selection.enabled && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(input) => {
                        if (input)
                          input.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                )}
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    style={{ width: column.width }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (selection.enabled ? 1 : 0)}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id || index} className="hover:bg-gray-50">
                    {selection.enabled && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.some(
                            (selected) => selected.id === item.id
                          )}
                          onChange={(e) =>
                            handleSelectItem(item, e.target.checked)
                          }
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                      >
                        {renderCell(column, item)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pagination.enabled && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Mostrando {startItem} a {endItem} de {filteredData.length}{" "}
                  resultados
                </div>
                {pagination.showItemsPerPageSelector && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Mostrar:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-700">por página</span>
                  </div>
                )}
              </div>
              {totalPages > 1 && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);
